import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => (v == null ? "—" : new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v));

const METHODS = [
  ["none", "Tatsächlich"],
  ["base", "Pauschal 1,8 %"],
  ["branch_ra", "RA-Branche 1,7 %"],
];

// Stammdaten: Vorsteuer-Methode (§ 14 UStG), Vorjahresumsatz (auto/manuell), Jahres-Höchstbetrag.
export const LexVstpPanel = ({ clientId }) => {
  const [st, setSt] = useState(null);
  const [prior, setPrior] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/lex/advisor/clients/${clientId}/vstp-status`)
      .then((r) => { setSt(r.data); setPrior(r.data.prior_year_revenue_manual ?? ""); })
      .catch(() => setSt(null));
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const call = async (path, body, msg) => {
    setSaving(true);
    try {
      await axios.post(`${API}/lex/advisor/clients/${clientId}/${path}`, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally { setSaving(false); }
  };

  if (!st) return null;
  const pct = st.cap ? Math.min(100, (st.used_ytd / st.cap) * 100) : 0;
  const manual = st.prior_year_revenue_manual != null;

  return (
    <div className="space-y-3" data-testid="lex-vstp-panel">
      <div>
        <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Vorsteuer</p>
        <div className="inline-flex border border-slate-200 flex-wrap">
          {METHODS.map(([val, label]) => (
            <button key={val} type="button" disabled={saving}
              onClick={() => val !== st.method && call("vstp-method", { method: val }, `Vorsteuer-Methode: ${label}.`)}
              className="px-3 py-1.5 text-xs uppercase tracking-wider font-semibold transition-colors disabled:opacity-60"
              style={val === st.method ? { background: "#0A1128", color: "#C5A059" } : { background: "#fff", color: "#64748B" }}
              data-testid={`lex-vstp-method-${val}`}>
              {val === st.method && <Check className="h-3 w-3 inline mr-1 -mt-0.5" />}{label}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-1">§ 14 UStG · Wechsel nur zum Jahresbeginn, Bindung mind. 2 Jahre.</p>
      </div>

      {st.method !== "none" && (
        <>
          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-1.5">Vorjahresumsatz {st.year - 1}</p>
            <div className="flex gap-2 items-center">
              <input type="number" min="0" step="1" value={prior} onChange={(e) => setPrior(e.target.value)}
                placeholder={`automatisch: ${fmtEur(st.prior_year_revenue_auto)}`}
                className="max-w-[220px] w-full border border-slate-200 px-3 py-1.5 text-sm lex-mono focus:outline-none focus:border-[#C5A059]"
                data-testid="lex-vstp-prior-input" />
              <button type="button" disabled={saving || String(prior) === String(st.prior_year_revenue_manual ?? "")}
                onClick={() => call("prior-year-revenue", { amount: prior === "" ? null : Number(prior) }, "Vorjahresumsatz gespeichert.")}
                className="text-xs uppercase tracking-wider font-semibold px-3 py-1.5 border border-slate-200 text-slate-600 hover:border-[#C5A059] disabled:opacity-50"
                data-testid="lex-vstp-prior-save">Speichern</button>
              {manual && (
                <button type="button" disabled={saving} onClick={() => call("prior-year-revenue", { amount: null }, "Vorjahresumsatz wieder automatisch.")}
                  className="text-[11px] text-slate-500 underline" data-testid="lex-vstp-prior-reset">automatisch</button>
              )}
            </div>
            <p className="text-xs text-slate-400 mt-1" data-testid="lex-vstp-prior-hint">
              {manual ? `Manuell gesetzt · automatisch wären ${fmtEur(st.prior_year_revenue_auto)}` : "Automatisch aus vereinnahmten Honoraren"}
              {st.limit ? ` · Grenze ${fmtEur(st.limit)}` : ""}
            </p>
            {!st.eligible && (
              <p className="text-xs font-medium text-red-700 bg-red-50 p-2 mt-1" data-testid="lex-vstp-not-eligible">
                Vorjahresumsatz über der Grenze — {st.year} keine Pauschalierung, die UVA rechnet mit tatsächlicher Vorsteuer.
              </p>
            )}
          </div>
          {st.cap && st.eligible && (
            <div data-testid="lex-vstp-cap">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-slate-500">Pauschale {st.year} verbraucht</span>
                <span className="lex-mono font-semibold" style={{ color: pct >= 100 ? "#DC2626" : "#0A1128" }}>
                  {fmtEur(st.used_ytd)} / {fmtEur(st.cap)}
                </span>
              </div>
              <div className="h-1.5 bg-slate-100 w-full">
                <div className="h-1.5" style={{ width: `${pct}%`, background: pct >= 100 ? "#DC2626" : "#C5A059" }} />
              </div>
              {st.limit && st.ytd_base_net > st.limit && (
                <p className="text-xs text-amber-700 mt-1" data-testid="lex-vstp-next-year-hint">
                  Umsatz {st.year} über {fmtEur(st.limit)} — ab {st.year + 1} keine Pauschalierung mehr möglich.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LexVstpPanel;
